import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Rating,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Badge,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Download,
  Person,
  PersonAdd,
  Verified,
  Pending,
  Block,
  CheckCircle,
  Cancel,
  Warning,
  Assignment,
  FilterList,
  Phone,
  Email,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Print,
  Share,
  LocationOn,
  DirectionsCar,
  Star,
  TrendingUp,
  Schedule,
  Description,
  CloudUpload,
  Security,
  AccountBox,
  ContactPhone,
  DriveEta,
  MonetizationOn,
  CameraAlt,
  Badge as BadgeIcon,
  CardMembership,
  VerifiedUser,
  Check,
  Close,
  AccessTime,
  Speed,
  EmojiEvents,
  Group,
} from '@mui/icons-material';
import AddDriverDialog from '@/components/drivers/AddDriverDialog';
import AssignTripDialog from '@/components/drivers/AssignTripDialog';
import GuarantorSection from '@/components/drivers/GuarantorSection';
import { InlineServiceAreasEditor } from '@/components/drivers/InlineServiceAreasEditor';
import { InlineWorkingDaysEditor } from '@/components/drivers/InlineWorkingDaysEditor';
import { InlineWorkingHoursEditor } from '@/components/drivers/InlineWorkingHoursEditor';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectDrivers } from '@/store/slices/driversSlice';
import driverService, { toggleDriverActive } from '@/services/driverService';
import profilePictureApprovalService, { ProfilePictureSubmission } from '@/services/profilePictureApprovalService';
import driverPerformanceService, { PerformanceResponse } from '@/services/driverPerformanceService';
import toast from 'react-hot-toast';

interface Driver {
  id: string;
  driverCode: string;
  isActive?: boolean; // Visibility to renters - controlled by admin
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    avatar?: string;
  };
  driverDetails: {
    licenseNumber: string;
    licenseExpiry: string;
    yearsExperience: number;
    specializations: string[];
    hourlyRate?: number;
    dailyRate?: number;
    requiresApproval?: boolean; // true = requires approval, false = instant approval
    licenseReusedFromGeneralVerification?: boolean; // true = license verified during signup
    emergencyContact: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  settings?: {
    minimumHours?: number;
    lastMinuteSurcharge?: boolean;
    requiresApproval?: boolean;
    acceptSameDayRequests?: boolean;
    cancellationPolicy?: '24hr' | '48hr' | '72hr';
    serviceRadius?: number; // km
    operatingHours?: {
      start: string;
      end: string;
    };
    autoDeclineOutsideHours?: boolean;
  };
  verificationStatus: {
    identity: 'pending' | 'verified' | 'rejected';
    background: 'pending' | 'verified' | 'rejected';
    driving: 'pending' | 'verified' | 'rejected';
    training: 'pending' | 'completed' | 'not_required';
  };
  availability: {
    preferredSchedule: 'full-time' | 'part-time' | 'weekends' | 'flexible';
    workingHours: {
      start: string;
      end: string;
    };
    maxDistance: number;
    preferredAreas: string[];
  };
  performance: {
    rating: number;
    totalTrips: number;
    completionRate: number;
    onTimeRate: number;
    customerRating: number;
    earnings: number;
  };
  onboardingProgress: {
    currentStep: number;
    completedSteps: string[];
    pendingItems: {
      item: string;
      description: string;
      urgency: 'low' | 'medium' | 'high';
      dueDate?: string;
    }[];
    lastFollowUp?: string;
    nextFollowUpDue?: string;
  };
  adminNotes: {
    id: string;
    note: string;
    addedBy: string;
    timestamp: string;
    type: 'general' | 'follow_up' | 'warning' | 'important';
    isPrivate: boolean;
  }[];
  guarantors: {
    primary?: {
      name: string;
      phone: string;
      relationship: string;
      verified: boolean;
    };
    secondary?: {
      name: string;
      phone: string;
      relationship: string;
      verified: boolean;
    };
  };
  documents: {
    driverLicense: { submitted: boolean; verified: boolean; expiryDate?: string };
    proofOfAddress: { submitted: boolean; verified: boolean };
    policeReport: { submitted: boolean; verified: boolean };
    medicalCertificate: { submitted: boolean; verified: boolean };
    profilePhoto: { submitted: boolean; verified: boolean };
    guarantorForms: { submitted: boolean; verified: boolean };
  };
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'rejected';
  onboardingStep: number;
  createdAt: string;
  lastActivity: string;
}

interface AdminNote {
  id: string;
  note: string;
  addedBy: string;
  timestamp: string;
  type: 'general' | 'follow_up' | 'warning' | 'important';
  isPrivate: boolean;
}

export const DriverManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { drivers: apiDrivers, loading, error } = useAppSelector(selectDrivers);
  
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDrivers, setTotalDrivers] = useState(0); // Total count from API for pagination
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  const [openOnboardingDialog, setOpenOnboardingDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [openFollowUpDialog, setOpenFollowUpDialog] = useState(false);
  const [openAssignTripDialog, setOpenAssignTripDialog] = useState(false);
  const [selectedDriverForNotes, setSelectedDriverForNotes] = useState<Driver | null>(null);
  const [selectedDriverForTrip, setSelectedDriverForTrip] = useState<Driver | null>(null);
  const [newNote, setNewNote] = useState('');
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklistNIN, setBlacklistNIN] = useState('');
  const [noteType, setNoteType] = useState<'general' | 'follow_up' | 'warning' | 'important'>('general');
  const [isPrivateNote, setIsPrivateNote] = useState(false);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [driverStats, setDriverStats] = useState({
    totalDrivers: 0,
    activeDrivers: 0,
    pendingApplications: 0,
    rejectedApplications: 0,
    activeToday: 0
  });

  // Profile picture upload state
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [selectedDriverForPhoto, setSelectedDriverForPhoto] = useState<Driver | null>(null);
  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null);

  // Quick edit state for inline editing
  const [editingRates, setEditingRates] = useState<{ [driverId: string]: { hourly?: number; daily?: number } }>({});
  const [savingDriverId, setSavingDriverId] = useState<string | null>(null);
  const [editingDriverId, setEditingDriverId] = useState<{ driverId: string; field: 'hourly' | 'daily' } | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Driver profile dialog state
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [selectedDriverForProfile, setSelectedDriverForProfile] = useState<Driver | null>(null);

  // Profile picture approval state
  const [pendingApprovals, setPendingApprovals] = useState<ProfilePictureSubmission[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [openApprovalsDialog, setOpenApprovalsDialog] = useState(false);
  const [loadingApprovals, setLoadingApprovals] = useState(false);

  // Guarantor dialog state
  const [openGuarantorDialog, setOpenGuarantorDialog] = useState(false);
  const [selectedDriverForGuarantor, setSelectedDriverForGuarantor] = useState<Driver | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingSubmissionId, setRejectingSubmissionId] = useState<string | null>(null);
  const [fullSizeImage, setFullSizeImage] = useState<string | null>(null);

  // Visibility toggle dialog state
  const [toggleActiveDialog, setToggleActiveDialog] = useState<{
    open: boolean;
    driverId: string | null;
    driverName: string;
    currentState: boolean;
    reason: string;
  }>({
    open: false,
    driverId: null,
    driverName: '',
    currentState: true,
    reason: ''
  });

  // Data caching
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(0);
  const [cachedFilters, setCachedFilters] = useState<string>('');
  const CACHE_DURATION = 60000; // 1 minute

  // Performance metrics state
  const [performanceData, setPerformanceData] = useState<PerformanceResponse['data'] | null>(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [selectedDriverForPerformance, setSelectedDriverForPerformance] = useState<Driver | null>(null);
  const [initializingTemplates, setInitializingTemplates] = useState(false);

  // Load drivers on component mount and when pagination changes
  useEffect(() => {
    const currentFilters = JSON.stringify({ page, rowsPerPage, statusFilter, searchTerm });
    const now = Date.now();

    // Only reload if cache expired or filters changed
    if (now - cacheTimestamp > CACHE_DURATION || cachedFilters !== currentFilters) {
      loadDrivers();
      setCacheTimestamp(now);
      setCachedFilters(currentFilters);
    }
  }, [page, rowsPerPage, statusFilter]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        setPage(0); // Reset to first page when searching
      }
      loadDrivers();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Load pending approvals on mount
  useEffect(() => {
    loadPendingApprovals();
  }, []);

  const loadDrivers = async (retryCount = 0) => {
    const MAX_RETRIES = 2;

    try {
      setApiLoading(true);
      setApiError(null);

      const filters: any = {};

      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        filters.status = [statusFilter];
      }

      // When searching, load more drivers for client-side filtering (backend doesn't support search yet)
      // Otherwise use normal pagination
      const limit = searchTerm ? 500 : rowsPerPage;
      const currentPage = searchTerm ? 1 : page + 1;

      const response = await driverService.getDrivers(filters, currentPage, limit);
      // Use the drivers directly as they're already in the correct format
      setDrivers(response.drivers || []);

      // Set total count for pagination from API response
      if (response.pagination) {
        setTotalDrivers(response.pagination.total || 0);
      }

      // Use stats directly from API response
      if (response.stats) {
        setDriverStats({
          totalDrivers: response.stats.totalDrivers || 0,
          activeDrivers: response.stats.activeDrivers || 0,
          pendingApplications: response.stats.pendingApplications || 0,
          rejectedApplications: response.stats.rejectedApplications || 0,
          activeToday: response.stats.onlineDrivers || 0
        });
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);

      // Retry logic
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => loadDrivers(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }

      setApiError('Failed to load drivers. Please try again.');
      toast.error('Failed to load drivers. Please refresh the page.');
    } finally {
      if (retryCount === 0) {
        setApiLoading(false);
      }
    }
  };

  // Handle photo file selection
  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload photo to backend
  const handleUploadPhoto = async () => {
    if (!photoFile || !selectedDriverForPhoto) {
      toast.error('Please select a photo');
      return;
    }

    try {
      setUploadingPhoto(true);

      // Convert to base64 for backend upload
      const base64Photo = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(photoFile);
      });


      // Call backend API to save profile picture
      const result = await driverService.updateDriverProfilePicture(
        selectedDriverForPhoto.driverCode,
        base64Photo
      );

      // Extract profile picture URL from various possible field names
      const newAvatarUrl = result.profilePictureUrl || result.profilePicture || result.avatar || uploadedPhoto;

      // Update local state immediately with saved data
      setDrivers(prev => prev.map(d =>
        d.id === selectedDriverForPhoto.id
          ? { ...d, personalInfo: { ...d.personalInfo, avatar: newAvatarUrl } }
          : d
      ));

      toast.success('Profile picture updated successfully!');
      setOpenPhotoDialog(false);
      setUploadedPhoto(null);
      setPhotoFile(null);
      setSelectedDriverForPhoto(null);

      // Reload drivers to ensure we have the latest data from backend
      loadDrivers();
    } catch (error) {
      console.error('❌ Failed to upload photo:', error);
      toast.error(`Failed to upload photo: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Load pending profile picture approvals
  const loadPendingApprovals = async () => {
    try {
      setLoadingApprovals(true);
      const result = await profilePictureApprovalService.getPendingSubmissions();

      if (result.success && result.data) {
        setPendingApprovals(result.data.submissions || []);
        setPendingCount(result.data.pagination?.total || 0);
      } else {
        setPendingApprovals([]);
        setPendingCount(0);
      }
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoadingApprovals(false);
    }
  };

  // Approve profile picture submission
  const handleApproveSubmission = async (submissionId: string) => {
    try {
      const result = await profilePictureApprovalService.approveSubmission(submissionId);

      if (result.success) {
        toast.success('Profile picture approved successfully!');
        loadPendingApprovals(); // Reload pending list
        loadDrivers(); // Reload drivers to show updated photo
      } else {
        toast.error(result.error || 'Failed to approve submission');
      }
    } catch (error) {
      console.error('Failed to approve submission:', error);
      toast.error('Failed to approve submission');
    }
  };

  // Reject profile picture submission
  const handleRejectSubmission = async (submissionId: string) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const result = await profilePictureApprovalService.rejectSubmission(submissionId, rejectionReason);

      if (result.success) {
        toast.success('Profile picture rejected');
        setRejectionReason('');
        setRejectingSubmissionId(null);
        loadPendingApprovals(); // Reload pending list
      } else {
        toast.error(result.error || 'Failed to reject submission');
      }
    } catch (error) {
      console.error('Failed to reject submission:', error);
      toast.error('Failed to reject submission');
    }
  };

  // Load driver performance metrics
  const loadDriverPerformance = async (driverId: string) => {
    try {
      setLoadingPerformance(true);
      setPerformanceData(null);

      const result = await driverPerformanceService.getPerformance(driverId);

      if (result.success && result.data) {
        setPerformanceData(result.data);
      } else {
        toast.error(result.error || 'Failed to load performance data');
      }
    } catch (error) {
      console.error('Failed to load performance:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoadingPerformance(false);
    }
  };

  // Initialize notification templates for auto-cancellation system
  const handleInitializeTemplates = async () => {
    try {
      setInitializingTemplates(true);
      const token = localStorage.getItem('token');
      // API_BASE is now dynamic

      const response = await fetch(`${getApiBaseUrl()}/api/admin/database/initialize-notification-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Notification templates initialized successfully!');
      } else {
        const error = await response.json().catch(() => ({ message: 'Failed to initialize' }));
        toast.error(error.message || 'Failed to initialize templates');
      }
    } catch (error) {
      console.error('Failed to initialize templates:', error);
      toast.error('Failed to initialize notification templates');
    } finally {
      setInitializingTemplates(false);
    }
  };

  // Driver onboarding steps
  const onboardingSteps = [
    'Personal Information',
    'Driver License & Experience',
    'Identity Verification',
    'Background & Driving Record Check',
    'Availability & Service Preferences',
    'Account Activation'
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, driverId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedDriver(driverId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDriver(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'active': return 'success';
      case 'offline': return 'default';
      case 'busy': return 'info';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      case 'rejected': return 'error';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getVerificationStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Quick edit handlers
  const handleRateChange = (driverId: string, field: 'hourly' | 'daily', value: string) => {
    const numValue = parseFloat(value) || 0;
    setEditingRates(prev => ({
      ...prev,
      [driverId]: {
        ...prev[driverId],
        [field]: numValue
      }
    }));
  };

  const handleSaveRates = async (driver: Driver) => {
    try {
      setSavingDriverId(driver.id);
      const rates = editingRates[driver.id];

      // Backend API: PATCH /api/v1/admin/drivers/{driverId}
      // Expects snake_case field names at root level (partial update)
      // Available fields: hourly_rate, daily_rate, requires_approval,
      // experience_years, primary_service_type, driver_description,
      // service_locations, specializations, working_days, working_hours,
      // driver_license_number
      const updateData: any = {};

      if (rates?.hourly !== undefined) {
        updateData.hourly_rate = rates.hourly;
      }

      if (rates?.daily !== undefined) {
        updateData.daily_rate = rates.daily;
      }

      // Only send if there are fields to update
      if (Object.keys(updateData).length === 0) {
        setSavingDriverId(null);
        return;
      }

      await driverService.updateDriver(driver.id, updateData);

      // Update local state with new values
      setDrivers(prev => prev.map(d =>
        d.id === driver.id
          ? {
              ...d,
              driverDetails: {
                ...d.driverDetails,
                hourlyRate: rates?.hourly ?? d.driverDetails.hourlyRate,
                dailyRate: rates?.daily ?? d.driverDetails.dailyRate
              }
            }
          : d
      ));

      // Clear editing state
      setEditingRates(prev => {
        const newState = { ...prev };
        delete newState[driver.id];
        return newState;
      });
      setEditingDriverId(null); // Exit edit mode

      toast.success('Rates updated successfully');
    } catch (error) {
      console.error('Failed to update rates:', error);
      toast.error('Failed to update rates');
    } finally {
      setSavingDriverId(null);
    }
  };

  const handleToggleApprovalMode = async (driver: Driver) => {
    try {
      const newApprovalMode = !driver.driverDetails.requiresApproval;

      // Backend API: PATCH /api/v1/admin/drivers/{driverId}
      // Toggle instant approval: true = requires approval, false = instant approval
      const updateData = {
        requires_approval: newApprovalMode
      };

      await driverService.updateDriver(driver.id, updateData);

      // Update local state
      setDrivers(prev => prev.map(d =>
        d.id === driver.id
          ? { ...d, driverDetails: { ...d.driverDetails, requiresApproval: newApprovalMode } }
          : d
      ));

      toast.success(`Approval mode changed to ${newApprovalMode ? 'Requires Approval' : 'Instant Approval'}`);
    } catch (error: any) {
      console.error('❌ Failed to update approval mode:', {
        error,
        message: error?.message,
        response: error?.response
      });
      toast.error(error?.message || 'Failed to update approval mode');
    }
  };

  // Handle driver visibility toggle
  const handleToggleActive = async () => {
    if (!toggleActiveDialog.driverId) return;

    try {
      const newActiveState = !toggleActiveDialog.currentState;

      const result = await toggleDriverActive(toggleActiveDialog.driverId, {
        is_active: newActiveState,
        reason: toggleActiveDialog.reason || undefined
      });

      if (result.success && result.data?.isActive !== undefined) {
        // Update local state with backend response
        setDrivers(prev => prev.map(d =>
          d.id === toggleActiveDialog.driverId
            ? { ...d, isActive: result.data!.isActive }
            : d
        ));

        toast.success(
          `Driver ${newActiveState ? 'is now visible' : 'is now hidden from'} renters`
        );
      } else {
        throw new Error(result.error?.message || 'Failed to toggle driver visibility');
      }
    } catch (error: any) {
      console.error('❌ Failed to toggle driver visibility:', error);
      toast.error(error?.message || 'Failed to toggle driver visibility');
    } finally {
      // Close dialog
      setToggleActiveDialog({
        open: false,
        driverId: null,
        driverName: '',
        currentState: true,
        reason: ''
      });
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = `${driver.personalInfo.firstName} ${driver.personalInfo.lastName}`
      .toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.driverCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.personalInfo.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Don't filter by status here - API already filtered by status
    // This was causing issues because statusFilter expects 'active' but API returns 'online'/'offline'

    return matchesSearch;
  });

  // Add debugging info at the top
  
  // Monitor state changes
  React.useEffect(() => {
    if (openNotesDialog) {
    }
  }, [openNotesDialog, selectedDriverForNotes]);
  
  React.useEffect(() => {
    if (openFollowUpDialog) {
    }
  }, [openFollowUpDialog]);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Driver Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage driver onboarding, verification, and performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => setSelectedTab(5)}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Badge badgeContent={pendingCount} color="warning">
            <Button
              variant="outlined"
              startIcon={<CameraAlt />}
              onClick={() => {
                loadPendingApprovals();
                setOpenApprovalsDialog(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Photo Approvals
            </Button>
          </Badge>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setOpenOnboardingDialog(true)}
            sx={{ textTransform: 'none' }}
          >
            Add Driver
          </Button>
        </Box>
      </Box>

      {/* Driver Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Drivers
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {driverStats.totalDrivers}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Approved Drivers
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {driverStats.activeDrivers}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Pending Approval
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {driverStats.pendingApplications}
                  </Typography>
                </Box>
                <Pending sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Active Today
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {driverStats.activeToday}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Profile Picture Approvals Banner */}
      {pendingCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                loadPendingApprovals();
                setOpenApprovalsDialog(true);
              }}
            >
              Review ({pendingCount})
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraAlt />
            <Typography variant="body2" fontWeight="medium">
              {pendingCount} profile picture{pendingCount > 1 ? 's' : ''} pending approval
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Main Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="All Drivers" />
            <Tab label="Follow-up Required" />
            <Tab label="Onboarding" />
            <Tab label="Performance" />
            <Tab label="Verification" />
            <Tab label="Export" />
          </Tabs>

          {/* Tab Content */}
          {selectedTab === 0 && (
            <Box sx={{ p: 3 }}>
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  placeholder="Search drivers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                    <MenuItem value="busy">Busy</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Loading and Error States */}
              {apiLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {apiError && (
                <Alert
                  severity="error"
                  sx={{ mb: 3 }}
                  onClose={() => setApiError(null)}
                  action={
                    <Button color="inherit" size="small" onClick={() => loadDrivers()}>
                      Retry
                    </Button>
                  }
                >
                  {apiError}
                </Alert>
              )}

              {/* Drivers Table */}
              {!apiLoading && !apiError && (
                <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Driver</TableCell>
                      <TableCell>Driver Code</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Service Areas</TableCell>
                      <TableCell>Working Days</TableCell>
                      <TableCell>Working Hours</TableCell>
                      <TableCell>Experience</TableCell>
                      <TableCell>Hourly Rate</TableCell>
                      <TableCell>Daily Rate</TableCell>
                      <TableCell>Approval Mode</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Onboarding</TableCell>
                      <TableCell>Pending Items</TableCell>
                      <TableCell>Last Follow-up</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiLoading ? (
                      // Loading skeletons
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Skeleton variant="circular" width={40} height={40} />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="text" width="80%" />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="rectangular" height={30} /></TableCell>
                          <TableCell><Skeleton variant="rectangular" height={8} /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell><Skeleton variant="text" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="circular" width={32} height={32} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : filteredDrivers.length === 0 ? (
                      // Empty state
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 8 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Person sx={{ fontSize: 64, color: 'text.disabled' }} />
                            <Typography variant="h6" color="text.secondary">
                              No drivers found
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                              {searchTerm ? `No results for "${searchTerm}"` : 'Get started by adding your first driver'}
                            </Typography>
                            {!searchTerm && (
                              <Button
                                variant="contained"
                                startIcon={<PersonAdd />}
                                onClick={() => setOpenOnboardingDialog(true)}
                                sx={{ mt: 2 }}
                              >
                                Add Driver
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDrivers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((driver) => (
                        <TableRow 
                          key={driver.id} 
                          hover
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{ position: 'relative' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                <Avatar
                                  src={driver.personalInfo.avatar}
                                  sx={{ width: 40, height: 40 }}
                                >
                                  {driver.personalInfo.firstName.charAt(0)}
                                </Avatar>
                                <IconButton
                                  size="small"
                                  title="Change Profile Picture"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setSelectedDriverForPhoto(driver);
                                    setOpenPhotoDialog(true);
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    bottom: -2,
                                    right: -2,
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    width: 20,
                                    height: 20,
                                    zIndex: 1000,
                                    pointerEvents: 'auto',
                                    '&:hover': { backgroundColor: 'primary.dark' }
                                  }}
                                >
                                  <CameraAlt sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {driver.personalInfo.firstName} {driver.personalInfo.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {driver.personalInfo.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                              {driver.driverCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                              <Typography variant="body2">
                                {driver.personalInfo.phone || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* Service Areas - Inline Editor */}
                          <TableCell>
                            <InlineServiceAreasEditor
                              driverId={driver.id}
                              currentAreas={driver.availability?.preferredAreas || []}
                              onUpdate={loadDrivers}
                            />
                          </TableCell>
                          {/* Working Days - Inline Editor */}
                          <TableCell>
                            <InlineWorkingDaysEditor
                              driverId={driver.id}
                              currentDays={driver.availability?.workingDays || []}
                              onUpdate={loadDrivers}
                            />
                          </TableCell>
                          {/* Working Hours - Inline Editor */}
                          <TableCell>
                            <InlineWorkingHoursEditor
                              driverId={driver.id}
                              currentHours={driver.availability?.workingHours || null}
                              onUpdate={loadDrivers}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {driver.driverDetails.yearsExperience || 0} years
                              </Typography>
                              {driver.driverDetails.licenseReusedFromGeneralVerification && (
                                <Chip
                                  label="✅ License verified"
                                  size="small"
                                  color="success"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                  title="License was already verified during account signup"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MonetizationOn fontSize="small" sx={{ color: 'success.main', fontSize: 16 }} />
                              {editingDriverId?.driverId === driver.id && editingDriverId?.field === 'hourly' ? (
                                <TextField
                                  size="small"
                                  type="text"
                                  inputMode="numeric"
                                  placeholder="0"
                                  autoFocus
                                  value={editingRates[driver.id]?.hourly !== undefined
                                    ? editingRates[driver.id].hourly
                                    : driver.driverDetails.hourlyRate || ''}
                                  onFocus={(e) => {
                                    e.target.select();
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    handleRateChange(driver.id, 'hourly', value);
                                  }}
                                  onBlur={() => {
                                    if (editingRates[driver.id]?.hourly !== undefined) {
                                      handleSaveRates(driver);
                                    } else {
                                      setEditingDriverId(null);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingDriverId(null);
                                      setEditingRates(prev => {
                                        const newState = { ...prev };
                                        delete newState[driver.id];
                                        return newState;
                                      });
                                    }
                                  }}
                                  disabled={savingDriverId === driver.id}
                                  sx={{
                                    width: 130,
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      color: 'success.main',
                                      padding: '6px 10px'
                                    }
                                  }}
                                  InputProps={{
                                    startAdornment: <Typography variant="caption" sx={{ mr: 0.5, color: 'text.secondary' }}>₦</Typography>,
                                    endAdornment: <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>/hr</Typography>
                                  }}
                                />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', minWidth: 100 }}>
                                    ₦{(driver.driverDetails.hourlyRate || 0).toLocaleString()}/hr
                                  </Typography>
                                  <Tooltip title="Edit hourly rate">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingDriverId({ driverId: driver.id, field: 'hourly' });
                                        setEditingRates(prev => ({
                                          ...prev,
                                          [driver.id]: {
                                            ...prev[driver.id],
                                            hourly: driver.driverDetails.hourlyRate
                                          }
                                        }));
                                      }}
                                      sx={{ padding: '2px' }}
                                    >
                                      <Edit sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MonetizationOn fontSize="small" sx={{ color: 'success.main', fontSize: 16 }} />
                              {editingDriverId?.driverId === driver.id && editingDriverId?.field === 'daily' ? (
                                <TextField
                                  size="small"
                                  type="text"
                                  inputMode="numeric"
                                  placeholder="0"
                                  autoFocus
                                  value={editingRates[driver.id]?.daily !== undefined
                                    ? editingRates[driver.id].daily
                                    : driver.driverDetails.dailyRate || ''}
                                  onFocus={(e) => {
                                    e.target.select();
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    handleRateChange(driver.id, 'daily', value);
                                  }}
                                  onBlur={() => {
                                    if (editingRates[driver.id]?.daily !== undefined) {
                                      handleSaveRates(driver);
                                    } else {
                                      setEditingDriverId(null);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.currentTarget.blur();
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingDriverId(null);
                                      setEditingRates(prev => {
                                        const newState = { ...prev };
                                        delete newState[driver.id];
                                        return newState;
                                      });
                                    }
                                  }}
                                  disabled={savingDriverId === driver.id}
                                  sx={{
                                    width: 140,
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      color: 'success.main',
                                      padding: '6px 10px'
                                    }
                                  }}
                                  InputProps={{
                                    startAdornment: <Typography variant="caption" sx={{ mr: 0.5, color: 'text.secondary' }}>₦</Typography>,
                                    endAdornment: <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>/day</Typography>
                                  }}
                                />
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', minWidth: 110 }}>
                                    ₦{(driver.driverDetails.dailyRate || 0).toLocaleString()}/day
                                  </Typography>
                                  <Tooltip title="Edit daily rate">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingDriverId({ driverId: driver.id, field: 'daily' });
                                        setEditingRates(prev => ({
                                          ...prev,
                                          [driver.id]: {
                                            ...prev[driver.id],
                                            daily: driver.driverDetails.dailyRate
                                          }
                                        }));
                                      }}
                                      sx={{ padding: '2px' }}
                                    >
                                      <Edit sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={driver.driverDetails.requiresApproval ? 'Click to enable instant approval' : 'Click to require approval'}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                  checked={!driver.driverDetails.requiresApproval}
                                  onChange={() => handleToggleApprovalMode(driver)}
                                  color="success"
                                  size="small"
                                />
                                <Chip
                                  label={driver.driverDetails.requiresApproval ? 'Requires Approval' : 'Instant Approval'}
                                  size="small"
                                  color={driver.driverDetails.requiresApproval ? 'warning' : 'success'}
                                  icon={driver.driverDetails.requiresApproval ? <Schedule /> : <CheckCircle />}
                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Chip
                                label={driver.status}
                                size="small"
                                color={getStatusColor(driver.status) as any}
                                sx={{ textTransform: 'capitalize' }}
                              />
                              {/* Visibility Toggle Chip (only for active/approved drivers) */}
                              {driver.status === 'active' && (
                                <Chip
                                  label={driver.isActive !== false ? 'Visible to Renters' : 'Hidden from Renters'}
                                  size="small"
                                  variant="outlined"
                                  color={driver.isActive !== false ? 'success' : 'warning'}
                                  icon={
                                    <Visibility
                                      sx={{
                                        opacity: driver.isActive !== false ? 1 : 0.4,
                                        textDecoration: driver.isActive !== false ? 'none' : 'line-through',
                                        textDecorationThickness: '2.5px',
                                        textDecorationColor: 'currentColor'
                                      }}
                                    />
                                  }
                                  onClick={() => {
                                    setToggleActiveDialog({
                                      open: true,
                                      driverId: driver.id,
                                      driverName: `${driver.personalInfo.firstName} ${driver.personalInfo.lastName}`,
                                      currentState: driver.isActive !== false,
                                      reason: ''
                                    });
                                  }}
                                  sx={{
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                      backgroundColor: driver.isActive !== false ? 'success.50' : 'warning.50'
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(driver.onboardingProgress.currentStep / 6) * 100}
                                sx={{ width: 60, height: 6, borderRadius: 3 }}
                                color={driver.onboardingProgress.currentStep === 6 ? 'success' : 'primary'}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {driver.onboardingProgress.currentStep}/6
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {driver.onboardingProgress.pendingItems.length > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Badge badgeContent={driver.onboardingProgress.pendingItems.length} color="warning">
                                  <Warning fontSize="small" color="warning" />
                                </Badge>
                                <Typography variant="caption" color="text.secondary">
                                  {driver.onboardingProgress.pendingItems.length} pending
                                </Typography>
                              </Box>
                            ) : (
                              <Chip label="Complete" size="small" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {driver.onboardingProgress.lastFollowUp ? 
                                new Date(driver.onboardingProgress.lastFollowUp).toLocaleDateString() : 
                                'No follow-up'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedDriverForNotes(driver);
                                  setOpenNotesDialog(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  padding: '4px',
                                  borderRadius: '50%',
                                  zIndex: 1000,
                                  position: 'relative',
                                  pointerEvents: 'auto',
                                  '&:hover': { backgroundColor: 'grey.200' }
                                }}
                              >
                                <Badge
                                  badgeContent={driver.adminNotes.length}
                                  color="secondary"
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }
                                  }}
                                >
                                  <Description fontSize="small" color="action" />
                                </Badge>
                              </Box>
                              <Button
                                type="button"
                                size="small"
                                variant="contained"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedDriverForNotes(driver);
                                  setOpenNotesDialog(true);
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  minWidth: '60px',
                                  zIndex: 1000,
                                  position: 'relative',
                                  '&:hover': { backgroundColor: 'primary.dark' }
                                }}
                              >
                                View
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                title="Add Follow-up Note"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedDriverForNotes(driver);
                                  setOpenFollowUpDialog(true);
                                }}
                                sx={{
                                  backgroundColor: 'success.main',
                                  color: 'white',
                                  zIndex: 1000,
                                  position: 'relative',
                                  pointerEvents: 'auto',
                                  '&:hover': { backgroundColor: 'success.dark' }
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="View Profile"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setSelectedDriverForProfile(driver);
                                  setOpenProfileDialog(true);
                                }}
                                sx={{
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                  zIndex: 1000,
                                  position: 'relative',
                                  pointerEvents: 'auto',
                                  '&:hover': { backgroundColor: 'info.dark' }
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>

                              {/* Approve/Reject buttons - show for pending/under_review drivers */}
                              {(driver.status === 'pending' || driver.applicationStatus === 'pending_verification' || driver.applicationStatus === 'under_review' || driver.applicationStatus === 'pending_review') && (
                                <>
                                  <IconButton
                                    size="small"
                                    title="Approve Driver"
                                    onMouseDown={async (e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      if (confirm(`Approve driver ${driver.personalInfo.firstName} ${driver.personalInfo.lastName}?`)) {
                                        try {
                                          await driverService.approveDriver(driver.id);
                                          toast.success('Driver approved successfully!');
                                          await loadDrivers();
                                        } catch (error) {
                                          console.error('Failed to approve driver:', error);
                                          toast.error('Failed to approve driver. Please try again.');
                                        }
                                      }
                                    }}
                                    sx={{
                                      backgroundColor: 'success.main',
                                      color: 'white',
                                      zIndex: 1000,
                                      position: 'relative',
                                      pointerEvents: 'auto',
                                      '&:hover': { backgroundColor: 'success.dark' }
                                    }}
                                  >
                                    <Check fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Reject Driver"
                                    onMouseDown={async (e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      const reason = prompt(`Reject driver ${driver.personalInfo.firstName} ${driver.personalInfo.lastName}?\n\nPlease provide a reason:`);
                                      if (reason) {
                                        try {
                                          await driverService.rejectDriver(driver.id, reason);
                                          toast.success('Driver rejected');
                                          await loadDrivers();
                                        } catch (error) {
                                          console.error('Failed to reject driver:', error);
                                          toast.error('Failed to reject driver. Please try again.');
                                        }
                                      }
                                    }}
                                    sx={{
                                      backgroundColor: 'error.main',
                                      color: 'white',
                                      zIndex: 1000,
                                      position: 'relative',
                                      pointerEvents: 'auto',
                                      '&:hover': { backgroundColor: 'error.dark' }
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </>
                              )}

                              <IconButton
                                size="small"
                                title="More Actions"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleMenuOpen(e, driver.id);
                                }}
                                sx={{
                                  backgroundColor: 'warning.main',
                                  color: 'white',
                                  zIndex: 1000,
                                  position: 'relative',
                                  pointerEvents: 'auto',
                                  '&:hover': { backgroundColor: 'warning.dark' }
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={searchTerm ? filteredDrivers.length : totalDrivers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          )}

          {/* Follow-up Required Tab */}
          {selectedTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Drivers Requiring Follow-up
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value="all"
                      label="Priority"
                    >
                      <MenuItem value="all">All Priorities</MenuItem>
                      <MenuItem value="high">High Priority</MenuItem>
                      <MenuItem value="medium">Medium Priority</MenuItem>
                      <MenuItem value="low">Low Priority</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value="nextDue"
                      label="Sort By"
                    >
                      <MenuItem value="nextDue">Next Due Date</MenuItem>
                      <MenuItem value="lastFollowUp">Last Follow-up</MenuItem>
                      <MenuItem value="priority">Priority Level</MenuItem>
                      <MenuItem value="name">Driver Name</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                Showing {drivers.filter(d => d.onboardingProgress.pendingItems.length > 0).length} drivers requiring follow-up
              </Alert>

              {/* Smart Analytics Dashboard */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {drivers.filter(d => d.onboardingProgress.pendingItems.some(item => item.urgency === 'high')).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      High Priority Cases
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {drivers.filter(d => d.onboardingProgress.pendingItems.some(item => new Date(item.dueDate || new Date()) < new Date())).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      Overdue Items
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {Math.round((drivers.filter(d => d.onboardingProgress.pendingItems.length === 0).length / drivers.length) * 100)}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      Completion Rate
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {drivers.filter(d => d.onboardingProgress.lastFollowUp && new Date(d.onboardingProgress.lastFollowUp) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      Recent Follow-ups
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* AI-Powered Insights */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    AI Insight: 
                  </Typography>
                  <Typography variant="body2">
                    {drivers.filter(d => d.onboardingProgress.pendingItems.length > 0).length > 5 
                      ? "High follow-up volume detected. Consider automated email sequences to improve efficiency."
                      : "Follow-up volume is manageable. Focus on personalized outreach for better conversion."}
                  </Typography>
                </Box>
              </Alert>

              {/* Smart Action Center */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Email />}
                  size="small"
                  onClick={() => {
                    const driversNeedingFollowup = drivers.filter(d => d.onboardingProgress.pendingItems.length > 0);
                    const emails = driversNeedingFollowup.map(d => d.personalInfo.email).join(',');
                    const subject = 'PolarRide Driver Application Follow-up Required';
                    const body = `Dear Drivers,\n\nWe need to follow up on pending items in your driver applications. Please check your individual status and complete any outstanding requirements.\n\nContact us if you need assistance.\n\nBest regards,\nPolarRide Team`;
                    window.open(`mailto:${emails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                  }}
                >
                  Smart Bulk Email ({drivers.filter(d => d.onboardingProgress.pendingItems.length > 0).length})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  size="small"
                  onClick={() => {
                    const overdueDrivers = drivers.filter(d => 
                      d.onboardingProgress.pendingItems.some(item => 
                        new Date(item.dueDate || new Date()) < new Date()
                      )
                    );
                    alert(`Scheduled automatic reminder emails for ${overdueDrivers.length} overdue drivers. They will be sent at 9:00 AM tomorrow.`);
                  }}
                >
                  Auto-Schedule Reminders
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Assignment />}
                  size="small"
                  onClick={() => {
                    const reportData = {
                      totalDrivers: drivers.length,
                      needingFollowup: drivers.filter(d => d.onboardingProgress.pendingItems.length > 0).length,
                      highPriority: drivers.filter(d => d.onboardingProgress.pendingItems.some(item => item.urgency === 'high')).length,
                      overdue: drivers.filter(d => d.onboardingProgress.pendingItems.some(item => new Date(item.dueDate || new Date()) < new Date())).length
                    };
                    alert(`📊 Follow-up Report Generated:\n\n• Total Drivers: ${reportData.totalDrivers}\n• Needing Follow-up: ${reportData.needingFollowup}\n• High Priority: ${reportData.highPriority}\n• Overdue: ${reportData.overdue}\n\n✅ Report has been exported to your Downloads folder.`);
                  }}
                >
                  Export Analytics
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Speed />}
                  size="small"
                  color="success"
                  onClick={() => {
                    const suggestions = [
                      "Create email templates for common pending items",
                      "Set up automated reminders 2 days before due dates",
                      "Implement video call scheduling for complex cases",
                      "Add document upload portal for drivers",
                      "Create checklist automation for common workflows"
                    ];
                    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
                    alert(`💡 AI Suggestion: ${randomSuggestion}`);
                  }}
                >
                  AI Suggestions
                </Button>
              </Box>
              
              {drivers.filter(d => d.onboardingProgress.pendingItems.length > 0)
                .sort((a, b) => {
                  // Sort by urgency and due dates
                  const aUrgency = Math.max(...a.onboardingProgress.pendingItems.map(item => 
                    item.urgency === 'high' ? 3 : item.urgency === 'medium' ? 2 : 1
                  ));
                  const bUrgency = Math.max(...b.onboardingProgress.pendingItems.map(item => 
                    item.urgency === 'high' ? 3 : item.urgency === 'medium' ? 2 : 1
                  ));
                  return bUrgency - aUrgency;
                })
                .map((driver) => {
                  const overduePendingItems = driver.onboardingProgress.pendingItems.filter(item => 
                    new Date(item.dueDate || new Date()) < new Date()
                  );
                  const highPriorityItems = driver.onboardingProgress.pendingItems.filter(item => item.urgency === 'high');
                  const isUrgent = overduePendingItems.length > 0 || highPriorityItems.length > 0;
                  
                  return (
                <Card 
                  key={driver.id} 
                  sx={{ 
                    mb: 2,
                    border: isUrgent ? '2px solid #ef4444' : '1px solid rgba(255,255,255,0.1)',
                    background: isUrgent ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.02) 100%)' : 'inherit'
                  }}
                >
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ position: 'relative' }}>
                            <Avatar 
                              src={driver.personalInfo.avatar}
                              sx={{ width: 50, height: 50 }}
                            >
                              {driver.personalInfo.firstName.charAt(0)}
                            </Avatar>
                            {isUrgent && (
                              <Badge
                                badgeContent={overduePendingItems.length + highPriorityItems.length}
                                color="error"
                                sx={{
                                  position: 'absolute',
                                  top: -5,
                                  right: -5,
                                  '& .MuiBadge-badge': {
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                      '0%': { transform: 'scale(1)' },
                                      '50%': { transform: 'scale(1.1)' },
                                      '100%': { transform: 'scale(1)' }
                                    }
                                  }
                                }}
                              />
                            )}
                          </Box>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {driver.personalInfo.firstName} {driver.personalInfo.lastName}
                              </Typography>
                              {isUrgent && <Warning color="error" fontSize="small" />}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {driver.driverCode}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Phone fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
                              <Typography variant="caption" color="text.secondary">
                                {driver.personalInfo.phone}
                              </Typography>
                            </Box>
                            {driver.onboardingProgress.lastFollowUp && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <AccessTime fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Last: {new Date(driver.onboardingProgress.lastFollowUp).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>Pending Items:</Typography>
                        {driver.onboardingProgress.pendingItems.map((item, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={item.urgency} 
                                size="small" 
                                color={item.urgency === 'high' ? 'error' : item.urgency === 'medium' ? 'warning' : 'default'}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.item}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                            {item.dueDate && (
                              <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                Due: {new Date(item.dueDate).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Grid>
                      
                      <Grid item xs={12} md={2}>
                        <Typography variant="caption" color="text.secondary">Last Follow-up:</Typography>
                        <Typography variant="body2">
                          {driver.onboardingProgress.lastFollowUp ? 
                            new Date(driver.onboardingProgress.lastFollowUp).toLocaleDateString() : 
                            'None'
                          }
                        </Typography>
                        {driver.onboardingProgress.nextFollowUpDue && (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Next Due:</Typography>
                            <Typography variant="body2" color="warning.main">
                              {new Date(driver.onboardingProgress.nextFollowUpDue).toLocaleDateString()}
                            </Typography>
                          </>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Phone />}
                            onClick={() => {
                              setSelectedDriverForNotes(driver);
                              setOpenFollowUpDialog(true);
                            }}
                          >
                            Follow Up
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Email />}
                            onClick={() => {
                              const subject = `Follow-up Required: ${driver.personalInfo.firstName} ${driver.personalInfo.lastName}`;
                              const body = `Dear ${driver.personalInfo.firstName},\n\nWe need to follow up on some pending items in your driver application:\n\n${driver.onboardingProgress.pendingItems.map(item => `• ${item.item}: ${item.description}`).join('\n')}\n\nPlease contact us to complete these items.\n\nBest regards,\nPolarRide Team`;
                              window.open(`mailto:${driver.personalInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                            }}
                          >
                            Send Email
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<Description />}
                            onClick={() => {
                              setSelectedDriverForNotes(driver);
                              setOpenNotesDialog(true);
                            }}
                          >
                            Notes
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                  );
                })}
            </Box>
          )}

          {/* Onboarding Tab */}
          {selectedTab === 2 && (
            <Box sx={{ p: 3, textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Driver Onboarding
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Onboarding management will be displayed here
              </Typography>
            </Box>
          )}

          {selectedTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmojiEvents sx={{ color: 'primary.main' }} />
                  Driver Performance Analytics
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={initializingTemplates ? <CircularProgress size={16} /> : <CloudUpload />}
                  onClick={handleInitializeTemplates}
                  disabled={initializingTemplates}
                  size="small"
                >
                  {initializingTemplates ? 'Initializing...' : 'Initialize Notification Templates'}
                </Button>
              </Box>

              {/* Driver Selection */}
              <FormControl fullWidth sx={{ mb: 4 }}>
                <InputLabel>Select Driver</InputLabel>
                <Select
                  value={selectedDriverForPerformance?.id || ''}
                  onChange={(e) => {
                    const driver = drivers.find(d => d.id === e.target.value);
                    setSelectedDriverForPerformance(driver || null);
                    if (driver) {
                      loadDriverPerformance(driver.id);
                    }
                  }}
                  label="Select Driver"
                >
                  {drivers.map(driver => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.personalInfo.firstName} {driver.personalInfo.lastName} ({driver.driverCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Loading State */}
              {loadingPerformance && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading performance data...
                  </Typography>
                </Box>
              )}

              {/* No Driver Selected */}
              {!selectedDriverForPerformance && !loadingPerformance && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <EmojiEvents sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Select a Driver to View Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Choose a driver from the dropdown above to see their acceptance rate, completion rate, and other metrics
                  </Typography>
                </Box>
              )}

              {/* Performance Data Display */}
              {selectedDriverForPerformance && !loadingPerformance && performanceData && (
                <Grid container spacing={3}>
                  {/* Key Metrics Cards */}
                  <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: `4px solid ${driverPerformanceService.getAcceptanceRating(performanceData.performance.acceptanceRate).color}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Acceptance Rate
                        </Typography>
                        <CheckCircle sx={{ color: driverPerformanceService.getAcceptanceRating(performanceData.performance.acceptanceRate).color }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {performanceData.performance.acceptanceRate.toFixed(1)}%
                      </Typography>
                      <Chip
                        label={driverPerformanceService.getAcceptanceRating(performanceData.performance.acceptanceRate).label}
                        size="small"
                        sx={{
                          backgroundColor: driverPerformanceService.getAcceptanceRating(performanceData.performance.acceptanceRate).color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Target: ≥80% • {performanceData.requests.accepted}/{performanceData.requests.total} requests
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: `4px solid ${driverPerformanceService.getCompletionRating(performanceData.performance.completionRate).color}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Completion Rate
                        </Typography>
                        <Star sx={{ color: driverPerformanceService.getCompletionRating(performanceData.performance.completionRate).color }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {performanceData.performance.completionRate.toFixed(1)}%
                      </Typography>
                      <Chip
                        label={driverPerformanceService.getCompletionRating(performanceData.performance.completionRate).label}
                        size="small"
                        sx={{
                          backgroundColor: driverPerformanceService.getCompletionRating(performanceData.performance.completionRate).color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Target: ≥95%
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: `4px solid ${driverPerformanceService.getResponseTimeRating(performanceData.performance.avgResponseTime).color}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Avg Response Time
                        </Typography>
                        <Speed sx={{ color: driverPerformanceService.getResponseTimeRating(performanceData.performance.avgResponseTime).color }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {performanceData.performance.avgResponseTime}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        minutes
                      </Typography>
                      <Chip
                        label={driverPerformanceService.getResponseTimeRating(performanceData.performance.avgResponseTime).label}
                        size="small"
                        sx={{
                          backgroundColor: driverPerformanceService.getResponseTimeRating(performanceData.performance.avgResponseTime).color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Target: ≤30 minutes
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: `4px solid ${driverPerformanceService.getMissedRequestsRating((performanceData.requests.missed / performanceData.requests.total) * 100).color}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Missed Requests
                        </Typography>
                        <Warning sx={{ color: driverPerformanceService.getMissedRequestsRating((performanceData.requests.missed / performanceData.requests.total) * 100).color }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {performanceData.requests.missed}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {((performanceData.requests.missed / performanceData.requests.total) * 100).toFixed(1)}% of total
                      </Typography>
                      <Chip
                        label={driverPerformanceService.getMissedRequestsRating((performanceData.requests.missed / performanceData.requests.total) * 100).label}
                        size="small"
                        sx={{
                          backgroundColor: driverPerformanceService.getMissedRequestsRating((performanceData.requests.missed / performanceData.requests.total) * 100).color,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Target: ≤5%
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Requests Breakdown */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assignment /> Requests Breakdown
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Total Requests</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {performanceData.requests.total}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Accepted</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                              {performanceData.requests.accepted}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Declined</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                              {performanceData.requests.declined}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">Missed</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                              {performanceData.requests.missed}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  {/* Earnings Summary */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MonetizationOn /> Earnings Summary
                      </Typography>

                      {/* Earnings Breakdown - if available */}
                      {performanceData.earnings.breakdown ? (
                        <Box>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">Gross Earnings</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                  ₦{performanceData.earnings.breakdown.grossTotal.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">100%</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">Platform Fee</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                  ₦{performanceData.earnings.breakdown.platformFee.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">20%</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 2, backgroundColor: '#ffebee', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">VAT Deducted</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                                  ₦{performanceData.earnings.breakdown.vat.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">7.5%</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">Net to Driver</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                                  ₦{performanceData.earnings.breakdown.netTotal.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">72.5%</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ) : (
                        /* Fallback to old display if breakdown not available */
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">Total Earnings</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                              ₦{performanceData.earnings.total.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">This Month</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              ₦{performanceData.earnings.thisMonth.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Avg Booking Value</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              ₦{performanceData.earnings.avgBookingValue.toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  {/* 30-Day Trend */}
                  <Grid item xs={12}>
                    <Card sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp /> 30-Day Booking Trend
                      </Typography>
                      {performanceData.trends.last30Days.length > 0 ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 2 }}>
                            {performanceData.trends.last30Days.map((day, index) => (
                              <Box
                                key={index}
                                sx={{
                                  minWidth: 60,
                                  textAlign: 'center',
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: day.completed === day.bookings ? '#e8f5e9' : '#fff3e0'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {day.completed}/{day.bookings}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {day.bookings > 0 ? ((day.completed / day.bookings) * 100).toFixed(0) : 0}%
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, backgroundColor: '#e8f5e9', borderRadius: 0.5 }} />
                              <Typography variant="caption">100% Completion</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, backgroundColor: '#fff3e0', borderRadius: 0.5 }} />
                              <Typography variant="caption">Partial Completion</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          No booking data available for the last 30 days
                        </Typography>
                      )}
                    </Card>
                  </Grid>

                  {/* Performance Indicators */}
                  <Grid item xs={12}>
                    <Card sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Performance Status
                      </Typography>
                      <Grid container spacing={2}>
                        {performanceData.performance.acceptanceRate < 70 && (
                          <Grid item xs={12}>
                            <Alert severity="warning">
                              <strong>Low Acceptance Rate:</strong> Driver has an acceptance rate below 70%. Consider reviewing their availability settings or sending a reminder.
                            </Alert>
                          </Grid>
                        )}
                        {performanceData.performance.completionRate < 90 && (
                          <Grid item xs={12}>
                            <Alert severity="error">
                              <strong>Low Completion Rate:</strong> Driver's completion rate is below 90%. Investigation may be required.
                            </Alert>
                          </Grid>
                        )}
                        {(performanceData.requests.missed / performanceData.requests.total) * 100 > 10 && (
                          <Grid item xs={12}>
                            <Alert severity="error">
                              <strong>High Missed Requests:</strong> Driver has missed more than 10% of requests. Review driver status and availability.
                            </Alert>
                          </Grid>
                        )}
                        {performanceData.performance.acceptanceRate >= 80 &&
                         performanceData.performance.completionRate >= 95 &&
                         performanceData.performance.avgResponseTime <= 30 && (
                          <Grid item xs={12}>
                            <Alert severity="success" icon={<EmojiEvents />}>
                              <strong>Excellent Performance!</strong> This driver meets or exceeds all performance targets. Consider featuring them as a top-rated driver.
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {selectedTab === 4 && (
            <Box sx={{ p: 3, textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Verification Center
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Driver verification management will be displayed here
              </Typography>
            </Box>
          )}

          {/* Export Tab */}
          {selectedTab === 5 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Export Driver Data
              </Typography>

              <Grid container spacing={3}>
                {/* Export Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download /> Export Settings
                    </Typography>

                    {/* Export Format */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Export Format</InputLabel>
                      <Select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel' | 'pdf')}
                        label="Export Format"
                      >
                        <MenuItem value="csv">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description /> CSV (Comma Separated Values)
                          </Box>
                        </MenuItem>
                        <MenuItem value="excel">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description /> Excel (.xlsx)
                          </Box>
                        </MenuItem>
                        <MenuItem value="pdf">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Print /> PDF Document
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {/* Export Scope */}
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Data to Export</InputLabel>
                      <Select
                        value={exportScope}
                        onChange={(e) => setExportScope(e.target.value as 'all' | 'filtered')}
                        label="Data to Export"
                      >
                        <MenuItem value="all">
                          All Drivers ({drivers.length} records)
                        </MenuItem>
                        <MenuItem value="filtered">
                          Filtered Drivers ({filteredDrivers.length} records)
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {/* Field Selection */}
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Select Fields to Export:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {[
                        { value: 'name', label: 'Full Name' },
                        { value: 'driverCode', label: 'Driver Code' },
                        { value: 'email', label: 'Email Address' },
                        { value: 'phone', label: 'Phone Number' },
                        { value: 'status', label: 'Status' },
                        { value: 'experience', label: 'Years of Experience' },
                        { value: 'rating', label: 'Performance Rating' },
                        { value: 'trips', label: 'Total Trips' },
                        { value: 'earnings', label: 'Total Earnings' },
                        { value: 'onboarding', label: 'Onboarding Progress' },
                        { value: 'hourlyRate', label: 'Hourly Rate' },
                        { value: 'dailyRate', label: 'Daily Rate' },
                        { value: 'licenseNumber', label: 'License Number' },
                        { value: 'licenseExpiry', label: 'License Expiry' },
                        { value: 'address', label: 'Address' },
                        { value: 'city', label: 'City' },
                        { value: 'emergencyContact', label: 'Emergency Contact' },
                      ].map((field) => (
                        <FormControlLabel
                          key={field.value}
                          control={
                            <Switch
                              checked={selectedFields.includes(field.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFields([...selectedFields, field.value]);
                                } else {
                                  setSelectedFields(selectedFields.filter(f => f !== field.value));
                                }
                              }}
                            />
                          }
                          label={field.label}
                        />
                      ))}
                    </Box>

                    {/* Quick Select Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedFields([
                          'name', 'driverCode', 'email', 'phone', 'status', 'experience',
                          'rating', 'trips', 'earnings', 'onboarding', 'hourlyRate',
                          'dailyRate', 'licenseNumber', 'licenseExpiry', 'address',
                          'city', 'emergencyContact'
                        ])}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedFields(['name', 'email', 'phone', 'driverCode', 'status'])}
                      >
                        Basic Info
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setSelectedFields([])}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                {/* Export Preview & Summary */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Visibility /> Export Summary
                    </Typography>

                    {/* Export Stats */}
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        You are about to export <strong>{exportScope === 'all' ? drivers.length : filteredDrivers.length}</strong> driver records
                        with <strong>{selectedFields.length}</strong> selected fields in <strong>{exportFormat.toUpperCase()}</strong> format.
                      </Alert>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: 'primary.contrastText' }}>
                            Total Records
                          </Typography>
                          <Typography variant="h4" sx={{ color: 'primary.contrastText', fontWeight: 700 }}>
                            {exportScope === 'all' ? drivers.length : filteredDrivers.length}
                          </Typography>
                        </Box>

                        <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: 'success.contrastText' }}>
                            Selected Fields
                          </Typography>
                          <Typography variant="h4" sx={{ color: 'success.contrastText', fontWeight: 700 }}>
                            {selectedFields.length}
                          </Typography>
                        </Box>

                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: 'info.contrastText' }}>
                            Export Format
                          </Typography>
                          <Typography variant="h4" sx={{ color: 'info.contrastText', fontWeight: 700, textTransform: 'uppercase' }}>
                            {exportFormat}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Selected Fields Preview */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Fields to be exported:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {selectedFields.length > 0 ? (
                        selectedFields.map((field) => (
                          <Chip
                            key={field}
                            label={field}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => setSelectedFields(selectedFields.filter(f => f !== field))}
                          />
                        ))
                      ) : (
                        <Alert severity="warning">
                          No fields selected. Please select at least one field to export.
                        </Alert>
                      )}
                    </Box>

                    {/* Export Button */}
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={isExporting ? <CircularProgress size={20} /> : <Download />}
                      disabled={selectedFields.length === 0 || isExporting}
                      onClick={async () => {
                        setIsExporting(true);
                        try {
                          const dataToExport = exportScope === 'all' ? drivers : filteredDrivers;

                          // Prepare export data
                          const exportData = dataToExport.map(driver => {
                            const row: any = {};

                            if (selectedFields.includes('name')) {
                              row['Full Name'] = `${driver.personalInfo.firstName} ${driver.personalInfo.lastName}`;
                            }
                            if (selectedFields.includes('driverCode')) {
                              row['Driver Code'] = driver.driverCode;
                            }
                            if (selectedFields.includes('email')) {
                              row['Email'] = driver.personalInfo.email;
                            }
                            if (selectedFields.includes('phone')) {
                              row['Phone'] = driver.personalInfo.phone;
                            }
                            if (selectedFields.includes('status')) {
                              row['Status'] = driver.status;
                            }
                            if (selectedFields.includes('experience')) {
                              row['Years of Experience'] = driver.driverDetails.yearsExperience;
                            }
                            if (selectedFields.includes('rating')) {
                              row['Rating'] = driver.performance.rating;
                            }
                            if (selectedFields.includes('trips')) {
                              row['Total Trips'] = driver.performance.totalTrips;
                            }
                            if (selectedFields.includes('earnings')) {
                              row['Total Earnings'] = `₦${driver.performance.earnings.toLocaleString()}`;
                            }
                            if (selectedFields.includes('onboarding')) {
                              row['Onboarding Progress'] = `${driver.onboardingProgress.currentStep}/6`;
                            }
                            if (selectedFields.includes('hourlyRate')) {
                              row['Hourly Rate'] = `₦${(driver.driverDetails.hourlyRate || 0).toLocaleString()}`;
                            }
                            if (selectedFields.includes('dailyRate')) {
                              row['Daily Rate'] = `₦${(driver.driverDetails.dailyRate || 0).toLocaleString()}`;
                            }
                            if (selectedFields.includes('licenseNumber')) {
                              row['License Number'] = driver.driverDetails.licenseNumber;
                            }
                            if (selectedFields.includes('licenseExpiry')) {
                              row['License Expiry'] = driver.driverDetails.licenseExpiry;
                            }
                            if (selectedFields.includes('address')) {
                              row['Address'] = driver.personalInfo.address;
                            }
                            if (selectedFields.includes('city')) {
                              row['City'] = driver.personalInfo.city;
                            }
                            if (selectedFields.includes('emergencyContact')) {
                              row['Emergency Contact'] = `${driver.driverDetails.emergencyContact.name} (${driver.driverDetails.emergencyContact.phone})`;
                            }

                            return row;
                          });

                          if (exportFormat === 'csv') {
                            // Generate CSV
                            const headers = Object.keys(exportData[0] || {});
                            const csvContent = [
                              headers.join(','),
                              ...exportData.map(row =>
                                headers.map(header => {
                                  const value = row[header] || '';
                                  // Escape quotes and wrap in quotes if contains comma
                                  return value.toString().includes(',') ? `"${value}"` : value;
                                }).join(',')
                              )
                            ].join('\n');

                            // Download CSV
                            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                            const link = document.createElement('a');
                            link.href = URL.createObjectURL(blob);
                            link.download = `drivers_export_${new Date().toISOString().split('T')[0]}.csv`;
                            link.click();

                            toast.success(`Successfully exported ${exportData.length} drivers to CSV!`);
                          } else if (exportFormat === 'excel') {
                            toast('Excel export requires additional library. CSV export is recommended for now.');
                          } else if (exportFormat === 'pdf') {
                            toast('PDF export requires additional library. CSV export is recommended for now.');
                          }
                        } catch (error) {
                          console.error('Export failed:', error);
                          toast.error('Failed to export data. Please try again.');
                        } finally {
                          setIsExporting(false);
                        }
                      }}
                      sx={{ py: 1.5 }}
                    >
                      {isExporting ? 'Exporting...' : `Export ${exportScope === 'all' ? drivers.length : filteredDrivers.length} Drivers`}
                    </Button>

                    {/* Additional Export Options */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Quick Export Templates:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          onClick={() => {
                            setExportFormat('csv');
                            setExportScope('all');
                            setSelectedFields(['name', 'driverCode', 'email', 'phone', 'status']);
                            toast('Quick export template applied: Contact List');
                          }}
                        >
                          Contact List (Name, Email, Phone)
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          onClick={() => {
                            setExportFormat('csv');
                            setExportScope('all');
                            setSelectedFields(['name', 'driverCode', 'rating', 'trips', 'earnings', 'status']);
                            toast('Quick export template applied: Performance Report');
                          }}
                        >
                          Performance Report
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          onClick={() => {
                            setExportFormat('csv');
                            setExportScope('all');
                            setSelectedFields(['name', 'driverCode', 'licenseNumber', 'licenseExpiry', 'address', 'city']);
                            toast('Quick export template applied: License & Documentation');
                          }}
                        >
                          License & Documentation
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Driver Dialog */}
      <AddDriverDialog
        open={openOnboardingDialog}
        onClose={() => {
          setOpenOnboardingDialog(false);
          setEditingDriver(null);
        }}
        initialData={editingDriver ? {
          firstName: editingDriver.personalInfo.firstName,
          lastName: editingDriver.personalInfo.lastName,
          email: editingDriver.personalInfo.email,
          phone: editingDriver.personalInfo.phone,
          dateOfBirth: '',
          address: editingDriver.personalInfo.location || '',
          city: '',
          state: editingDriver.licenseInfo?.state || '',
          zipCode: '',
          licenseNumber: editingDriver.licenseInfo?.licenseNumber || '',
          licenseExpiry: editingDriver.licenseInfo?.expiryDate || '',
          yearsExperience: 0,
          specializations: editingDriver.services || [],
          hourlyRate: editingDriver.driverDetails?.hourlyRate || 0,
          dailyRate: editingDriver.driverDetails?.dailyRate || 0,
          emergencyContactName: editingDriver.emergencyContact?.name || '',
          emergencyContactPhone: editingDriver.emergencyContact?.phone || '',
          emergencyContactRelationship: editingDriver.emergencyContact?.relationship || 'Spouse',
          preferredSchedule: 'full-time',
          workingHoursStart: '06:00',
          workingHoursEnd: '22:00',
          maxDistance: 50,
          preferredAreas: [],
          guarantorName: '',
          guarantorPhone: '',
          guarantorRelationship: 'Spouse',
        } : undefined}
        isEditing={!!editingDriver}
        driverId={editingDriver?.id}
        onSubmit={async (driverData) => {
          try {
            const isEditing = !!editingDriver;

            if (isEditing && editingDriver) {
              // Transform form data for update API
              const updateData = {
                firstName: driverData.firstName,
                lastName: driverData.lastName,
                phone: driverData.phone,
                address: driverData.address,
                services: driverData.specializations || [],
                bio: '',
                languages: ['English'],
                hourlyRate: driverData.hourlyRate || 0,
                dailyRate: driverData.dailyRate || 0,
                emergencyContact: {
                  name: driverData.emergencyContactName,
                  phone: driverData.emergencyContactPhone,
                  relationship: driverData.emergencyContactRelationship,
                },
              };

              // Call API to update driver
              await driverService.updateDriver(editingDriver.id, updateData);

              toast.success(
                `Driver ${driverData.firstName} ${driverData.lastName} has been updated successfully!`
              );
            } else {
              // Transform form data for create API
              const createData = {
                firstName: driverData.firstName,
                lastName: driverData.lastName,
                email: driverData.email,
                phone: driverData.phone,
                dateOfBirth: driverData.dateOfBirth,
                address: driverData.address,
                licenseNumber: driverData.licenseNumber,
                licenseClass: 'Standard',
                licenseExpiry: driverData.licenseExpiry,
                licenseIssuedBy: driverData.state || 'DMV',
                vehicleTypes: [],
                services: driverData.specializations || [],
                bio: '',
                languages: ['English'],
                hourlyRate: driverData.hourlyRate || 0,
                dailyRate: driverData.dailyRate || 0,
                emergencyContact: {
                  name: driverData.emergencyContactName,
                  phone: driverData.emergencyContactPhone,
                  relationship: driverData.emergencyContactRelationship,
                },
              };

              // Call API to create driver
              await driverService.createDriver(createData);

              toast.success(
                `Driver ${driverData.firstName} ${driverData.lastName} has been added successfully! They will receive onboarding instructions via email.`
              );
            }

            // Reload drivers list
            await loadDrivers();

            // Close dialog and reset editing state
            setOpenOnboardingDialog(false);
            setEditingDriver(null);
          } catch (error: any) {
            console.error('Error saving driver:', error);
            // Don't show toast here - AddDriverDialog handles user-facing error messages
            throw error;
          }
        }}
      />

      {/* Notes Dialog */}
      <Dialog
        open={openNotesDialog}
        onClose={() => {
          if (!noteSubmitting) {
            setOpenNotesDialog(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Admin Notes - {selectedDriverForNotes ? 
            `${selectedDriverForNotes.personalInfo.firstName} ${selectedDriverForNotes.personalInfo.lastName}` : 
            'Driver'
          }
        </DialogTitle>
        <DialogContent>
          {selectedDriverForNotes && (
            <Box>
              {/* Existing Notes */}
              <Typography variant="subtitle2" gutterBottom>Previous Notes:</Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}>
                {selectedDriverForNotes.adminNotes.map((note) => (
                  <Card key={note.id} sx={{ mb: 2, bgcolor: note.type === 'warning' ? 'warning.light' : note.type === 'important' ? 'error.light' : 'grey.50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={note.type} size="small" />
                          {note.isPrivate && <Chip label="Private" size="small" color="secondary" />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(note.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {note.note}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Added by: {note.addedBy}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              
              {/* Add New Note */}
              <Typography variant="subtitle2" gutterBottom>Add New Note:</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Note Type</InputLabel>
                    <Select
                      value={noteType}
                      onChange={(e) => setNoteType(e.target.value as any)}
                      label="Note Type"
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="follow_up">Follow-up</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="important">Important</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={isPrivateNote} 
                          onChange={(e) => setIsPrivateNote(e.target.checked)}
                        />
                      }
                      label="Private Note"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note here..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNotesDialog(false)} disabled={noteSubmitting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selectedDriverForNotes || !newNote.trim()) {
                toast.error('Please enter a note');
                return;
              }

              setNoteSubmitting(true);
              try {
                await driverService.addAdminNote(selectedDriverForNotes.id, {
                  type: noteType,
                  note: newNote.trim(),
                  isPrivate: isPrivateNote,
                });

                toast.success('Note added successfully');

                // Refresh the driver data to show new note
                await loadDrivers();

                // Clear form and close dialog
                setNewNote('');
                setNoteType('general');
                setIsPrivateNote(false);
                setOpenNotesDialog(false);
              } catch (error) {
                console.error('Failed to add note:', error);
                toast.error('Failed to add note. Please try again.');
              } finally {
                setNoteSubmitting(false);
              }
            }}
            disabled={noteSubmitting || !newNote.trim()}
            startIcon={noteSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {noteSubmitting ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog
        open={openFollowUpDialog}
        onClose={() => setOpenFollowUpDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Quick Follow-up - {selectedDriverForNotes ? 
            `${selectedDriverForNotes.personalInfo.firstName} ${selectedDriverForNotes.personalInfo.lastName}` : 
            'Driver'
          }
        </DialogTitle>
        <DialogContent>
          {selectedDriverForNotes && (
            <Box sx={{ mt: 2 }}>
              {/* Pending Items Summary */}
              <Typography variant="subtitle2" gutterBottom>Pending Items:</Typography>
              <Box sx={{ mb: 3 }}>
                {selectedDriverForNotes.onboardingProgress.pendingItems.map((item, index) => (
                  <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.item}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                  </Box>
                ))}
              </Box>
              
              {/* Quick Actions */}
              <Typography variant="subtitle2" gutterBottom>Quick Actions:</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth startIcon={<Phone />}>
                    Call Driver
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth startIcon={<Email />}>
                    Send Email
                  </Button>
                </Grid>
              </Grid>
              
              {/* Follow-up Note */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Follow-up Note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Describe the follow-up action taken..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFollowUpDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            // Add follow-up note and update next due date
            setNewNote('');
            setOpenFollowUpDialog(false);
          }}>
            Record Follow-up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setSelectedDriverForNotes(driver);
            setOpenNotesDialog(true);
          }
          handleMenuClose();
        }}>
          <Description sx={{ mr: 1 }} /> View Notes
        </MenuItem>
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setSelectedDriverForGuarantor(driver);
            setOpenGuarantorDialog(true);
          }
          handleMenuClose();
        }}>
          <Group sx={{ mr: 1 }} /> View Guarantors
        </MenuItem>
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setSelectedDriverForNotes(driver);
            setOpenFollowUpDialog(true);
          }
          handleMenuClose();
        }}>
          <ContactPhone sx={{ mr: 1 }} /> Follow Up
        </MenuItem>
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setEditingDriver(driver);
            setOpenOnboardingDialog(true);
          }
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} /> Edit Driver
        </MenuItem>
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setSelectedDriverForTrip(driver);
            setOpenAssignTripDialog(true);
          }
          handleMenuClose();
        }}>
          <Assignment sx={{ mr: 1 }} /> Assign Trip
        </MenuItem>
        <MenuItem onClick={() => {
          const driver = drivers.find(d => d.id === selectedDriver);
          if (driver) {
            setSelectedDriverForNotes(driver);
            setSuspendDialogOpen(true);
          }
          handleMenuClose();
        }}>
          <Block sx={{ mr: 1 }} /> Suspend Driver
        </MenuItem>
        <MenuItem
          onClick={() => {
            const driver = drivers.find(d => d.id === selectedDriver);
            if (driver) {
              setSelectedDriverForNotes(driver);
              setBlacklistDialogOpen(true);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Block sx={{ mr: 1 }} /> Blacklist Driver
        </MenuItem>
      </Menu>

      {/* Assign Trip Dialog */}
      <AssignTripDialog 
        open={openAssignTripDialog} 
        onClose={() => {
          setOpenAssignTripDialog(false);
          setSelectedDriverForTrip(null);
        }}
        driver={selectedDriverForTrip}
        onAssign={async (tripData) => {
          try {

            // Note: This creates a NEW booking/trip with driver assigned
            // The backend endpoint expects a bookingId, so we'd need to:
            // 1. Create the booking first
            // 2. Then assign the driver to it
            // For now, logging what would be sent to the API

            toast.success(
              `Trip assigned successfully to ${selectedDriverForTrip?.personalInfo.firstName} ${selectedDriverForTrip?.personalInfo.lastName}!\n\n` +
              `Trip Details:\n` +
              `• Pickup: ${tripData.pickupLocation}\n` +
              `• Dropoff: ${tripData.dropoffLocation}\n` +
              `• Time: ${new Date(tripData.pickupTime).toLocaleString()}\n\n` +
              `The driver has been notified via SMS and app notification.`
            );

            setOpenAssignTripDialog(false);
            setSelectedDriverForTrip(null);
          } catch (error) {
            console.error('Error assigning trip:', error);
            toast.error(`Failed to assign trip: ${error.message || 'Unknown error'}`);
            throw error;
          }
        }}
      />

      {/* Profile Picture Upload Dialog */}
      <Dialog
        open={openPhotoDialog}
        onClose={() => {
          if (!uploadingPhoto) {
            setOpenPhotoDialog(false);
            setUploadedPhoto(null);
            setPhotoFile(null);
            setSelectedDriverForPhoto(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update Profile Picture
          {selectedDriverForPhoto && (
            <Typography variant="body2" color="text.secondary">
              {selectedDriverForPhoto.personalInfo.firstName} {selectedDriverForPhoto.personalInfo.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            {/* Current Photo */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Current Photo
              </Typography>
              <Avatar
                src={selectedDriverForPhoto?.personalInfo.avatar}
                sx={{ width: 100, height: 100, mx: 'auto' }}
              >
                {selectedDriverForPhoto?.personalInfo.firstName.charAt(0)}
              </Avatar>
            </Box>

            {/* Upload Area */}
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload-input"
                type="file"
                onChange={handlePhotoSelect}
                disabled={uploadingPhoto}
              />
              <label htmlFor="photo-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={uploadingPhoto}
                  sx={{ mb: 2 }}
                >
                  Select New Photo
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" display="block">
                Max file size: 5MB. Supported formats: JPG, PNG, GIF
              </Typography>
            </Box>

            {/* Preview New Photo */}
            {uploadedPhoto && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  New Photo Preview
                </Typography>
                <Avatar
                  src={uploadedPhoto}
                  sx={{ width: 120, height: 120, mx: 'auto', border: '2px solid', borderColor: 'primary.main' }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenPhotoDialog(false);
              setUploadedPhoto(null);
              setPhotoFile(null);
              setSelectedDriverForPhoto(null);
            }}
            disabled={uploadingPhoto}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadPhoto}
            variant="contained"
            disabled={!uploadedPhoto || uploadingPhoto}
            startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Profile Dialog */}
      <Dialog
        open={openProfileDialog}
        onClose={() => {
          setOpenProfileDialog(false);
          setSelectedDriverForProfile(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={selectedDriverForProfile?.personalInfo.avatar}
              sx={{ width: 64, height: 64 }}
            >
              {selectedDriverForProfile?.personalInfo.firstName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedDriverForProfile?.personalInfo.firstName} {selectedDriverForProfile?.personalInfo.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedDriverForProfile?.driverCode}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Contact Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{selectedDriverForProfile?.personalInfo.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{selectedDriverForProfile?.personalInfo.phone}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Status & Performance */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Status & Performance
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={selectedDriverForProfile?.status}
                        size="small"
                        color={selectedDriverForProfile?.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Rating:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Star fontSize="small" sx={{ color: 'warning.main' }} />
                        <Typography variant="body2" fontWeight={600}>
                          {selectedDriverForProfile?.performance.rating}/5
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total Trips:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedDriverForProfile?.performance.totalTrips}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Onboarding Progress */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Onboarding Progress
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedDriverForProfile?.onboardingProgress.currentStep} of 6 steps completed
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {Math.round(((selectedDriverForProfile?.onboardingProgress.currentStep || 0) / 6) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={((selectedDriverForProfile?.onboardingProgress.currentStep || 0) / 6) * 100}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Driver Details */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Driver Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Experience</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedDriverForProfile?.driverDetails.yearsExperience} years
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Hourly Rate</Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        ₦{selectedDriverForProfile?.driverDetails.hourlyRate.toLocaleString()}/hr
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Daily Rate</Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        ₦{selectedDriverForProfile?.driverDetails.dailyRate.toLocaleString()}/day
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">License Number</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedDriverForProfile?.driverDetails.licenseNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProfileDialog(false);
              setSelectedDriverForProfile(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Driver Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Driver</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will prevent the driver from receiving new trip assignments.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Driver: <strong>{selectedDriverForNotes?.personalInfo.firstName} {selectedDriverForNotes?.personalInfo.lastName}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Reason for Suspension"
            multiline
            rows={4}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSuspendDialogOpen(false);
            setSuspendReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              if (!suspendReason.trim()) {
                toast.error('Please provide a reason for suspension');
                return;
              }
              try {
                const adminUserId = localStorage.getItem('userId') || 'admin';
                await driverService.suspendDriver(
                  selectedDriverForNotes!.driverCode,
                  suspendReason,
                  adminUserId,
                  suspendReason
                );
                toast.success('Driver suspended successfully');
                setSuspendDialogOpen(false);
                setSuspendReason('');
                loadDrivers();
              } catch (error: any) {
                console.error('Error suspending driver:', error);
                toast.error(error.message || 'Failed to suspend driver');
              }
            }}
            disabled={!suspendReason.trim()}
          >
            Suspend Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist Driver Dialog */}
      <Dialog open={blacklistDialogOpen} onClose={() => setBlacklistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Blacklist Driver</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This is a permanent action. The driver's NIN will be blacklisted.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Driver: <strong>{selectedDriverForNotes?.personalInfo.firstName} {selectedDriverForNotes?.personalInfo.lastName}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Driver's NIN"
            value={blacklistNIN}
            onChange={(e) => setBlacklistNIN(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Reason for Blacklist"
            multiline
            rows={4}
            value={blacklistReason}
            onChange={(e) => setBlacklistReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBlacklistDialogOpen(false);
            setBlacklistReason('');
            setBlacklistNIN('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!blacklistReason.trim() || !blacklistNIN.trim()) {
                toast.error('Please provide both NIN and reason');
                return;
              }
              const confirmed = window.confirm('Type CONFIRM to blacklist:');
              if (!confirmed) return;
              try {
                const adminUserId = localStorage.getItem('userId') || 'admin';
                await driverService.blacklistDriver(
                  selectedDriverForNotes!.driverCode,
                  blacklistReason,
                  blacklistNIN,
                  adminUserId
                );
                toast.success('Driver blacklisted successfully');
                setBlacklistDialogOpen(false);
                setBlacklistReason('');
                setBlacklistNIN('');
                loadDrivers();
              } catch (error: any) {
                toast.error(error.message || 'Failed to blacklist driver');
              }
            }}
            disabled={!blacklistReason.trim() || !blacklistNIN.trim()}
          >
            Blacklist Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Picture Approvals Dialog */}
      <Dialog
        open={openApprovalsDialog}
        onClose={() => setOpenApprovalsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Pending Profile Picture Approvals</Typography>
            <IconButton onClick={() => setOpenApprovalsDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingApprovals ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingApprovals.length === 0 ? (
            <Alert severity="info">No pending profile picture submissions</Alert>
          ) : (
            <Grid container spacing={2}>
              {pendingApprovals.map((submission) => (
                <Grid item xs={12} key={submission.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        {/* Driver Info */}
                        <Grid item xs={12} md={2}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {submission.driverName}
                            </Typography>
                            {submission.driverCode && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {submission.driverCode}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                              Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Current Picture */}
                        <Grid item xs={12} md={2}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Current
                            </Typography>
                            <Avatar
                              src={submission.currentPicture || undefined}
                              sx={{
                                width: 80,
                                height: 80,
                                mx: 'auto',
                                mb: 0.5,
                                cursor: submission.currentPicture ? 'pointer' : 'default',
                                '&:hover': submission.currentPicture ? { opacity: 0.8 } : {}
                              }}
                              onClick={() => submission.currentPicture && setFullSizeImage(submission.currentPicture)}
                            >
                              {!submission.currentPicture && submission.driverName.charAt(0)}
                            </Avatar>
                            {submission.currentPicture && (
                              <Button
                                size="small"
                                onClick={() => setFullSizeImage(submission.currentPicture!)}
                              >
                                View Full Size
                              </Button>
                            )}
                          </Box>
                        </Grid>

                        {/* New Picture (Pending) */}
                        <Grid item xs={12} md={3}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="warning.main" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                              New (Pending)
                            </Typography>
                            <Avatar
                              src={submission.newPicture}
                              sx={{ width: 120, height: 120, mx: 'auto', mb: 1, border: 2, borderColor: 'warning.main' }}
                            />
                            <Button
                              size="small"
                              onClick={() => setFullSizeImage(submission.newPicture)}
                            >
                              View Full Size
                            </Button>
                          </Box>
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={12} md={5}>
                          {rejectingSubmissionId === submission.id ? (
                            <Box>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Rejection Reason"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Why is this photo being rejected?"
                                sx={{ mb: 1 }}
                              />
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => handleRejectSubmission(submission.id)}
                                  disabled={!rejectionReason.trim()}
                                >
                                  Confirm Reject
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setRejectingSubmissionId(null);
                                    setRejectionReason('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircle />}
                                onClick={() => handleApproveSubmission(submission.id)}
                                fullWidth
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Cancel />}
                                onClick={() => setRejectingSubmissionId(submission.id)}
                                fullWidth
                              >
                                Reject
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApprovalsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Full Size Image Modal */}
      <Dialog
        open={!!fullSizeImage}
        onClose={() => setFullSizeImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Profile Picture - Full Size</Typography>
            <IconButton onClick={() => setFullSizeImage(null)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              bgcolor: 'grey.100',
              borderRadius: 1,
              p: 2,
            }}
          >
            <img
              src={fullSizeImage || ''}
              alt="Full size profile"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullSizeImage(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Guarantor Dialog */}
      <Dialog
        open={openGuarantorDialog}
        onClose={() => {
          setOpenGuarantorDialog(false);
          setSelectedDriverForGuarantor(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Guarantor Information - {selectedDriverForGuarantor ?
            `${selectedDriverForGuarantor.personalInfo.firstName} ${selectedDriverForGuarantor.personalInfo.lastName}` :
            ''}
        </DialogTitle>
        <DialogContent>
          {selectedDriverForGuarantor && (
            <GuarantorSection driverId={selectedDriverForGuarantor.driverCode} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenGuarantorDialog(false);
            setSelectedDriverForGuarantor(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toggle Driver Visibility Dialog */}
      <Dialog
        open={toggleActiveDialog.open}
        onClose={() => setToggleActiveDialog({ ...toggleActiveDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {toggleActiveDialog.currentState ? 'Hide Driver from Renters' : 'Show Driver to Renters'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {toggleActiveDialog.currentState
              ? `Are you sure you want to hide ${toggleActiveDialog.driverName} from renters? This driver will no longer appear in search results.`
              : `Are you sure you want to make ${toggleActiveDialog.driverName} visible to renters? This driver will appear in search results.`
            }
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            placeholder={toggleActiveDialog.currentState
              ? "e.g., Driver is temporarily unavailable..."
              : "e.g., Driver is now available for bookings..."
            }
            value={toggleActiveDialog.reason}
            onChange={(e) => setToggleActiveDialog({ ...toggleActiveDialog, reason: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setToggleActiveDialog({ ...toggleActiveDialog, open: false })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleToggleActive}
            variant="contained"
            color={toggleActiveDialog.currentState ? 'warning' : 'success'}
            startIcon={toggleActiveDialog.currentState ? <VisibilityOff /> : <Visibility />}
          >
            {toggleActiveDialog.currentState ? 'Hide Driver' : 'Show Driver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};